/**
 * Agent Trust Score & Probation Panel
 *
 * Demo Step 6: Show agent compliance score. Trigger a policy violation.
 * Watch the agent go on probation.
 *
 * Features:
 *   - Real-time compliance score gauge (0-100)
 *   - Autonomy level indicator with color coding
 *   - Live score recalculation on policy events
 *   - Animated probation transition when BLOCK is detected
 *   - Transition history timeline
 */

import React, { useState, useEffect, useRef } from 'react';
import { publishedBand, bandInk } from '../../services/trustBands';

const AUTONOMY_LEVELS = {
  SELF_AUTONOMOUS: { label: 'Self-Autonomous', color: '#16a34a', bg: '#dcfce7', icon: '🟢', threshold: 98, minEvals: 500 },
  SUPERVISED:      { label: 'Supervised', color: '#0d9488', bg: '#e3f4f1', icon: '🔵', threshold: 90, minEvals: 0 },
  RESTRICTED:      { label: 'Restricted', color: '#d97706', bg: '#fef3c7', icon: '🟡', threshold: 75, minEvals: 0 },
  PROBATION:       { label: 'On Probation', color: '#dc2626', bg: '#fef2f2', icon: '🔴', threshold: 0, minEvals: 0 },
};

export function AgentTrustScorePanel({ complianceEvents = [], agentData = null }) {
  const [score, setScore] = useState(agentData?.complianceScore || 96.5);
  const [previousScore, setPreviousScore] = useState(score);
  const [autonomyLevel, setAutonomyLevel] = useState(agentData?.autonomyLevel || 'SUPERVISED');
  const [previousLevel, setPreviousLevel] = useState(autonomyLevel);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitions, setTransitions] = useState([]);
  const [stats, setStats] = useState({
    totalEvaluations: agentData?.totalEvaluations || 342,
    blockCount: agentData?.blockCount || 0,
    warnCount: agentData?.warnCount || 3,
    auditCount: agentData?.auditCount || 8,
  });

  // Process incoming compliance events
  useEffect(() => {
    if (!complianceEvents.length) return;

    const latest = complianceEvents[complianceEvents.length - 1];

    if (latest.type === 'COMPLIANCE_SCORE_CHANGE') {
      setPreviousScore(score);
      setPreviousLevel(autonomyLevel);
      setScore(latest.newScore);

      if (latest.newLevel && latest.newLevel !== autonomyLevel) {
        setIsTransitioning(true);
        setTimeout(() => {
          setAutonomyLevel(latest.newLevel);
          setIsTransitioning(false);
        }, 1500); // Allow animation time

        setTransitions(prev => [{
          from: latest.previousLevel,
          to: latest.newLevel,
          previousScore: latest.previousScore,
          newScore: latest.newScore,
          timestamp: latest.timestamp,
          type: latest.newScore < latest.previousScore ? 'REGRESSION' : 'PROMOTION',
        }, ...prev]);
      }
    }

    if (latest.type === 'PROBATION_EVENT') {
      setIsTransitioning(true);
      setPreviousLevel(autonomyLevel);
      setTimeout(() => {
        setAutonomyLevel('PROBATION');
        setIsTransitioning(false);
      }, 1500);

      setTransitions(prev => [{
        from: autonomyLevel,
        to: 'PROBATION',
        reason: latest.triggerReason,
        probationUntil: latest.probationUntil,
        timestamp: latest.timestamp,
        type: 'PROBATION',
      }, ...prev]);
    }
  }, [complianceEvents]);

  const levelInfo = AUTONOMY_LEVELS[autonomyLevel] || AUTONOMY_LEVELS.SUPERVISED;
  const prevLevelInfo = AUTONOMY_LEVELS[previousLevel] || AUTONOMY_LEVELS.SUPERVISED;

  // Score gauge angle (0-180 degrees for semicircle)
  const gaugeAngle = (score / 100) * 180;
  /* The band this score falls in, not the autonomy thresholds it carried. This gauge shows a
   * trust score, so it is coloured by the band — and from the shared service, so it matches. */
  const gaugeColor = bandInk(publishedBand(score));

  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      fontSize: 13,
    }}>
      {/* Header */}
      <div style={{
        background: isTransitioning
          ? 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)'
          : 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)',
        color: '#fff',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        transition: 'background 0.5s ease',
      }}>
        <span style={{ fontSize: 16 }}>📊</span>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Agent Trust Score</span>
        <span style={{
          marginLeft: 'auto',
          background: levelInfo.bg,
          color: levelInfo.color,
          padding: '2px 10px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 700,
          transition: 'all 0.5s ease',
        }}>
          {levelInfo.icon} {levelInfo.label}
        </span>
      </div>

      {/* Score Display */}
      <div style={{ padding: '16px 18px', textAlign: 'center' }}>
        {/* Large Score Number */}
        <div style={{
          fontSize: 48,
          fontWeight: 800,
          color: gaugeColor,
          lineHeight: 1,
          transition: 'color 0.5s ease',
          position: 'relative',
        }}>
          {score.toFixed(1)}
          <span style={{ fontSize: 20, color: '#9ca3af' }}>%</span>

          {/* Score Change Indicator */}
          {previousScore !== score && (
            <span style={{
              position: 'absolute',
              top: 0,
              right: -40,
              fontSize: 14,
              color: score > previousScore ? '#16a34a' : '#dc2626',
              fontWeight: 600,
              animation: 'fadeIn 0.3s ease',
            }}>
              {score > previousScore ? '▲' : '▼'}
              {Math.abs(score - previousScore).toFixed(1)}
            </span>
          )}
        </div>

        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
          Rolling 30-day compliance score
        </div>

        {/* Threshold Markers */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '12px 0 0',
          position: 'relative',
          height: 4,
          background: '#e5e7eb',
          borderRadius: 2,
        }}>
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${score}%`,
            background: `linear-gradient(90deg, #dc2626 0%, #d97706 75%, #0d9488 90%, #16a34a 98%)`,
            borderRadius: 2,
            transition: 'width 0.8s ease',
          }} />
          {[75, 90, 98].map(t => (
            <div key={t} style={{
              position: 'absolute',
              left: `${t}%`,
              top: -2,
              width: 1,
              height: 8,
              background: '#374151',
            }}>
              <span style={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: 9,
                color: '#9ca3af',
              }}>
                {t}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        borderTop: '1px solid #e5e7eb',
        borderBottom: '1px solid #e5e7eb',
      }}>
        {[
          { label: 'Evaluations', value: stats.totalEvaluations, color: '#374151' },
          { label: 'Blocks', value: stats.blockCount, color: stats.blockCount > 0 ? '#dc2626' : '#374151' },
          { label: 'Warnings', value: stats.warnCount, color: stats.warnCount > 0 ? '#d97706' : '#374151' },
          { label: 'Audited', value: stats.auditCount, color: '#374151' },
        ].map((s, i) => (
          <div key={i} style={{
            padding: '8px',
            textAlign: 'center',
            borderRight: i < 3 ? '1px solid #e5e7eb' : 'none',
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 9, color: '#6b7280' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Transition Alert (shown during level change) */}
      {isTransitioning && (
        <div style={{
          padding: '12px 18px',
          background: '#fef2f2',
          borderBottom: '1px solid #fecaca',
          animation: 'pulse 1s ease infinite',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18, animation: 'spin 1s linear infinite' }}>⚠</span>
            <div>
              <div style={{ fontWeight: 700, color: '#991b1b', fontSize: 13 }}>
                AUTONOMY LEVEL TRANSITION
              </div>
              <div style={{ fontSize: 11, color: '#dc2626' }}>
                {prevLevelInfo.icon} {prevLevelInfo.label} → {AUTONOMY_LEVELS.PROBATION.icon} On Probation
              </div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                Single BLOCK violation triggered auto-regression. 7-day probation period starts now.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transition History */}
      {transitions.length > 0 && (
        <div style={{ padding: '10px 18px' }}>
          <div style={{ fontWeight: 600, color: '#374151', fontSize: 11, marginBottom: 6 }}>
            TRANSITION HISTORY
          </div>
          {transitions.slice(0, 5).map((t, i) => {
            const fromInfo = AUTONOMY_LEVELS[t.from] || {};
            const toInfo = AUTONOMY_LEVELS[t.to] || {};
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 0',
                borderBottom: i < transitions.length - 1 ? '1px dashed #f3f4f6' : 'none',
                fontSize: 11,
              }}>
                <span style={{ fontSize: 10, color: '#9ca3af' }}>
                  {new Date(t.timestamp).toLocaleTimeString()}
                </span>
                <span style={{ color: fromInfo.color }}>{fromInfo.icon}</span>
                <span style={{ color: '#9ca3af' }}>→</span>
                <span style={{ color: toInfo.color }}>{toInfo.icon} {toInfo.label}</span>
                {t.reason && (
                  <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 'auto' }}>
                    {t.reason}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AgentTrustScorePanel;
